import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import {
  stopAllBloodTestPolling,
  triggerBloodTestAnalysis,
  triggerBloodTestReanalysis,
  submitManualBloodTestText,
} from "@/lib/bloodTestPolling";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Droplets, X, Upload, Loader2, FileText, List, GitBranch } from "lucide-react";
import BloodTestDetail from "@/components/blood-tests/BloodTestDetail";
import BloodTestTrackEvolution from "@/components/blood-tests/BloodTestTrackEvolution";
import { REPORT_KIND_LABELS, UPLOAD_MODES } from "@/components/blood-tests/bloodTestLabels";

const PAGE_TABS = [
  { key: "reports", label: "Tous les bilans", icon: List },
  { key: "tracks", label: "Suivis", icon: GitBranch },
];

export default function BloodTests() {
  const [viewTab, setViewTab] = useState("reports");
  const [reports, setReports] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [uploadMode, setUploadMode] = useState("standalone");
  const [newTrackName, setNewTrackName] = useState("");
  const [selectedTrackForUpload, setSelectedTrackForUpload] = useState("");
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [useAiOcr, setUseAiOcr] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const fileRef = useRef(null);
  const pendingFileRef = useRef(null);

  const fetchReports = () => {
    api.get("/blood-tests")
      .then(r => setReports(r.data || []))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };

  const fetchTracks = () => {
    setTracksLoading(true);
    api.get("/blood-tests/tracks")
      .then(r => setTracks(r.data || []))
      .catch(() => {})
      .finally(() => setTracksLoading(false));
  };

  useEffect(() => {
    fetchReports();
    fetchTracks();
    return () => stopAllBloodTestPolling();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    api.get(`/blood-tests/${selectedId}`)
      .then(r => setDetail(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const resetUploadForm = () => {
    setFileData(null);
    setFileName("");
    setTitle("");
    setNotes("");
    setUploadMode("standalone");
    setNewTrackName("");
    setSelectedTrackForUpload("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Format non supporté (JPG, PNG, WEBP, PDF)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setFileData(ev.target.result);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const analysisCallbacks = (reportId) => ({
    onUpdate: (data) => {
      if (!data) return;
      setDetail(prev => (prev?.report_id === reportId || !prev ? { ...prev, ...data, report_id: reportId } : prev));
      setReports(prev => prev.map(r => (r.report_id === reportId ? { ...r, ...data } : r)));
    },
    fileDataUrl: pendingFileRef.current,
    useAiOcrFallback: useAiOcr,
    onNeedManualText: () => {
      setManualOpen(true);
      toast.info("Collez le texte de votre bilan pour continuer sans OCR IA");
    },
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fileData) return toast.error("Sélectionnez un fichier");

    if (uploadMode === "baseline" && !newTrackName.trim()) {
      return toast.error("Donnez un nom au suivi");
    }
    if (uploadMode === "followup" && !selectedTrackForUpload) {
      return toast.error("Sélectionnez un suivi existant");
    }

    setUploading(true);
    pendingFileRef.current = fileData;
    try {
      const payload = {
        file_data: fileData,
        title: title || "Bilan sanguin",
        notes: notes || null,
        report_kind: uploadMode,
      };
      if (uploadMode === "baseline") {
        payload.new_track_name = newTrackName.trim();
      }
      if (uploadMode === "followup") {
        payload.track_id = selectedTrackForUpload;
      }

      const { data } = await api.post("/blood-tests", payload, { timeout: 30000 });
      toast.success(
        uploadMode === "standalone"
          ? "Bilan uploadé — extraction en cours…"
          : "Bilan ajouté au suivi — extraction en cours…"
      );
      setOpen(false);
      resetUploadForm();
      setSelectedId(data.report_id);
      setDetail({ ...data, ai_status: "processing" });
      if (data.track_id) {
        setViewTab("tracks");
        setSelectedTrackId(data.track_id);
      }
      fetchReports();
      fetchTracks();
      triggerBloodTestAnalysis(data.report_id, analysisCallbacks(data.report_id))
        .then(() => { fetchReports(); fetchTracks(); pendingFileRef.current = null; });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.message || "Erreur lors de l'upload";
      toast.error(typeof msg === "string" ? msg : "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!selectedId || manualText.trim().length < 20) {
      return toast.error("Collez au moins 20 caractères de résultats");
    }
    setManualLoading(true);
    try {
      await submitManualBloodTestText(selectedId, manualText.trim(), analysisCallbacks(selectedId));
      setManualOpen(false);
      setManualText("");
      fetchReports();
      fetchTracks();
    } catch {
      toast.error("Erreur lors de l'analyse du texte collé");
    } finally {
      setManualLoading(false);
    }
  };

  const handleReanalyze = async () => {
    if (!selectedId) return;
    setReanalyzing(true);
    setDetail(prev => (prev ? { ...prev, ai_status: "processing" } : prev));
    await triggerBloodTestReanalysis(selectedId, analysisCallbacks(selectedId));
    fetchReports();
    fetchTracks();
    setReanalyzing(false);
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm("Supprimer ce bilan sanguin ?")) return;
    try {
      await api.delete(`/blood-tests/${selectedId}`);
      toast.success("Bilan supprimé");
      setSelectedId(null);
      fetchReports();
      fetchTracks();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSelectReport = (reportId) => {
    setSelectedId(reportId);
    setViewTab("reports");
  };

  const statusBadge = (status) => {
    const map = {
      done: { label: "Analysé", className: "bg-green-100 text-green-700" },
      processing: { label: "En cours", className: "bg-blue-100 text-blue-700" },
      extracted: { label: "Extrait", className: "bg-indigo-100 text-indigo-700" },
      parsed: { label: "Biomarqueurs OK", className: "bg-violet-100 text-violet-700" },
      pending: { label: "En attente", className: "bg-slate-100 text-slate-600" },
      error: { label: "Erreur", className: "bg-red-100 text-red-700" },
    };
    const cfg = map[status] || map.pending;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  const kindBadge = (kind) => {
    const cfg = REPORT_KIND_LABELS[kind] || REPORT_KIND_LABELS.standalone;
    return <Badge className={cfg.className}>{cfg.label}</Badge>;
  };

  return (
    <div data-testid="blood-tests-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <Droplets className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Bilan sanguin</h1>
            <p className="text-slate-500 mt-1">Bilans ponctuels ou suivis dans le temps — l'IA extrait et interprète les biomarqueurs</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetUploadForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="upload-blood-test-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau bilan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Uploader un bilan sanguin</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-2 block">Type d'upload</label>
                <div className="space-y-2">
                  {UPLOAD_MODES.map(mode => (
                    <label
                      key={mode.value}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        uploadMode === mode.value ? "border-red-200 bg-red-50/50" : "border-slate-100 hover:border-slate-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="uploadMode"
                        value={mode.value}
                        checked={uploadMode === mode.value}
                        onChange={() => setUploadMode(mode.value)}
                        className="mt-1"
                      />
                      <span>
                        <span className="text-sm font-medium text-slate-900 block">{mode.label}</span>
                        <span className="text-xs text-slate-500">{mode.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {uploadMode === "baseline" && (
                <Input
                  value={newTrackName}
                  onChange={e => setNewTrackName(e.target.value)}
                  placeholder="Nom du suivi (ex: Suivi lipides 2026)"
                  className="h-11"
                />
              )}

              {uploadMode === "followup" && (
                <Select value={selectedTrackForUpload} onValueChange={setSelectedTrackForUpload}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Choisir un suivi" /></SelectTrigger>
                  <SelectContent>
                    {tracks.length === 0 ? (
                      <SelectItem value="_none" disabled>Aucun suivi — créez-en un d'abord</SelectItem>
                    ) : (
                      tracks.map(t => (
                        <SelectItem key={t.track_id} value={t.track_id}>
                          {t.name} ({t.report_count} bilan{t.report_count > 1 ? "s" : ""})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}

              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" ref={fileRef} onChange={handleFileChange} className="hidden" />
              {fileData ? (
                <div className="relative bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                  <FileText className="w-8 h-8 text-red-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{fileName}</p>
                    <p className="text-xs text-slate-500">PDF ou image accepté</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setFileData(null); setFileName(""); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full h-36 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-red-300 hover:text-red-500 transition-colors"
                >
                  <Upload className="w-8 h-8 mb-2" />
                  <span className="text-sm">PDF ou photo du bilan</span>
                  <span className="text-xs mt-1">Max 10 Mo</span>
                </button>
              )}
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre (ex: Bilan annuel 2026)" className="h-11" />
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optionnel)" className="h-11" />
              <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={useAiOcr} onCheckedChange={v => setUseAiOcr(!!v)} className="mt-0.5" />
                <span>
                  Utiliser l'OCR IA (Mistral) si l'extraction locale échoue
                  <span className="block text-xs text-slate-400 mt-0.5">Désactivé par défaut — PDF texte et OCR navigateur sont essayés d'abord</span>
                </span>
              </label>
              <Button type="submit" disabled={uploading || !fileData} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full h-11">
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
                ) : (
                  "Uploader et analyser"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={manualOpen} onOpenChange={setManualOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Coller le texte du bilan</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500">
              L'extraction automatique n'a pas suffi. Copiez-collez les résultats depuis votre PDF ou le site du laboratoire.
            </p>
            <Textarea
              value={manualText}
              onChange={e => setManualText(e.target.value)}
              placeholder="Ex: Hémoglobine 14.2 g/dL (13-17), Glycémie 0.92 g/L..."
              className="min-h-[180px] font-mono text-sm"
            />
            <Button
              onClick={handleManualSubmit}
              disabled={manualLoading || manualText.trim().length < 20}
              className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full"
            >
              {manualLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse…</> : "Analyser ce texte"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-100">
        {PAGE_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setViewTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                viewTab === tab.key
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-3">
          {viewTab === "reports" ? (
            loading ? (
              <p className="text-slate-400 text-sm">Chargement…</p>
            ) : reports.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Droplets className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Aucun bilan. Uploadez votre première analyse !</p>
                </CardContent>
              </Card>
            ) : (
              reports.map(r => (
                <button
                  key={r.report_id}
                  onClick={() => setSelectedId(r.report_id)}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${
                    selectedId === r.report_id
                      ? "border-red-200 bg-red-50/50"
                      : "border-slate-100 bg-white hover:border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-medium text-slate-900 truncate">{r.title}</span>
                    <div className="flex gap-1.5 shrink-0">
                      {kindBadge(r.report_kind || "standalone")}
                      {statusBadge(r.ai_status)}
                    </div>
                  </div>
                  <div className="flex gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                    {r.lab_date && <span>{new Date(r.lab_date + "T12:00:00").toLocaleDateString("fr-FR")}</span>}
                    {r.marker_count > 0 && <span>{r.marker_count} marqueurs</span>}
                    {r.track_name && <span className="text-indigo-600">↳ {r.track_name}</span>}
                  </div>
                </button>
              ))
            )
          ) : tracksLoading ? (
            <p className="text-slate-400 text-sm">Chargement…</p>
          ) : tracks.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <GitBranch className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Aucun suivi. Démarrez-en un avec « Démarrer un suivi » à l'upload.</p>
              </CardContent>
            </Card>
          ) : (
            tracks.map(t => (
              <button
                key={t.track_id}
                onClick={() => { setSelectedTrackId(t.track_id); setSelectedId(null); }}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${
                  selectedTrackId === t.track_id
                    ? "border-red-200 bg-red-50/50"
                    : "border-slate-100 bg-white hover:border-slate-200"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900 truncate">{t.name}</span>
                  <Badge className="bg-red-100 text-red-700">{t.report_count} bilan{t.report_count > 1 ? "s" : ""}</Badge>
                </div>
                {t.last_lab_date && (
                  <p className="text-xs text-slate-500 mt-1">
                    Dernier : {new Date(t.last_lab_date + "T12:00:00").toLocaleDateString("fr-FR")}
                  </p>
                )}
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {viewTab === "tracks" && selectedTrackId ? (
            <BloodTestTrackEvolution
              trackId={selectedTrackId}
              onSelectReport={handleSelectReport}
            />
          ) : viewTab === "tracks" && !selectedTrackId ? (
            <div className="text-center py-16 text-slate-400">
              <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Sélectionnez un suivi pour voir l'évolution des marqueurs</p>
            </div>
          ) : !selectedId ? (
            <div className="text-center py-16 text-slate-400">
              <Droplets className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Sélectionnez un bilan pour voir l'analyse détaillée</p>
            </div>
          ) : (
            <BloodTestDetail
              report={detail}
              loading={detailLoading}
              onReanalyze={handleReanalyze}
              canDelete
              onDelete={handleDelete}
              reanalyzing={reanalyzing}
            />
          )}
        </div>
      </div>
    </div>
  );
}